import { Share, Tune } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import type React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import GoFundMeWidget from "../../../../../../../../components/GoFundMeWidget";
import { tokens } from "../../../../../../../../theme";
import type { DeceasedPerson } from "../../../../../../../bookings/types/edit-booking";
import TawkChat from "../../../../../../../landing/components/TawkChat";
import CommentCard from "../../../shared-ui/CommentCard";
import MemorialFooter from "../../../shared-ui/MemorialFooter";
import ShareControls from "../../../shared-ui/ShareControls";
import type { CalendarEvent } from "../../../types/AddtoCalendarutils";
import ShareDrawer from "../../../shared-ui/ShareDrawer";
import StreamViewer from "../../../shared-ui/StreamViewer";
import TemplateSelectorDialog from "../../../shared-ui/TemplateSelectorDialog";
import ThemedHeader from "../../../shared-ui/ThemedHeader";
import AdminTab from "../../../tabs/admin-tab/AdminTab";
import DonationsTab from "../../../tabs/DonationTab";
import { DownloadsTab } from "../../../tabs/DownloadsTab";
import GalleryTab from "../../../tabs/GalleryTab";
import MemoriesTab from "../../../tabs/MemoriesTab";
import type { ICommentType } from "../../../tabs/MemoriesTab";
import ObituaryTab from "../../../tabs/OrbituaryTab";
import FavouritesTab from "../../../tabs/FavouritesTab";
import TimelineTab from "../../../tabs/TimelineTab";
import type { IStreamDetails, IThemeConfig } from "../../../types/memorial-types";
import DonationSideCard from "../template-2/components/DonationSideCard";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: SxProps<Theme>;
}

function TabPanel({ children, value, index, sx, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`memorial-tabpanel-${index}`}
      aria-labelledby={`memorial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            width: "100%",
            p: 0,
            m: 0,
            ...(sx || {}),
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

export interface FuneralTemplate1Props {
  streams: IStreamDetails[];
  activeStreamIndex: number;
  currentStream: IStreamDetails;
  onStreamChange: (index: number) => void;
  memorialTheme: IThemeConfig;
  deceasedData: DeceasedPerson | null;
  isLoadingDeceased: boolean;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  onMessageSent: () => void;

  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;

  streamProgram: string | null;
  downloadingProgram: boolean;
  handleDownloadProgram: () => void;

  donationRefreshKey: number;
  fetchStreamDetails: () => Promise<void>;
  onDonationSuccess?: (donationId?: number) => void;

  isStaging: boolean;
  contactPhone?: string;
  contactEmail?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  whatsappUrl?: string;
  calendarEvent?: CalendarEvent | null;
}

export default function FuneralTemplate1({
  streams,
  activeStreamIndex,
  currentStream,
  onStreamChange,
  memorialTheme,
  deceasedData,
  isLoadingDeceased,
  messages,
  isAdmin,
  socketRef,
  handleApprove,
  handleDelete,
  onMessageSent,
  currentPage,
  totalCount,
  pageSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  streamProgram,
  downloadingProgram,
  handleDownloadProgram,
  donationRefreshKey,
  fetchStreamDetails,
  onDonationSuccess,
  isStaging,
  contactPhone,
  contactEmail,
  facebookUrl,
  linkedinUrl,
  youtubeUrl,
  whatsappUrl,
  calendarEvent,
}: FuneralTemplate1Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(1);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [highlightDonationId, setHighlightDonationId] = useState<number | undefined>(
    undefined,
  );
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Deep-link into the Donations tab after a successful redirect-based
  // payment (e.g. EFT Corp / ZimSwitch), and clear the params so a page
  // refresh doesn't keep re-highlighting the same donation.
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const donationIdParam = searchParams.get("donation_id");
    if (tabParam !== "donations") return;

    setTabValue(2);
    if (donationIdParam) {
      const parsed = Number(donationIdParam);
      if (!Number.isNaN(parsed)) setHighlightDonationId(parsed);
    }

    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.delete("donation_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDonationSuccess = (donationId?: number) => {
    setTabValue(2);
    if (donationId) setHighlightDonationId(donationId);
    onDonationSuccess?.(donationId);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const showChemaDonationCard = !isMobile && !!currentStream?.chema_fund_enabled;
  const showGoFundMeWidget =
    !isMobile &&
    !!currentStream?.go_fund_me_enabled &&
    !!currentStream?.go_fund_me_link;
  const hasSidePanel = showChemaDonationCard || showGoFundMeWidget;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: memorialTheme.surface }}>
      <TawkChat />
      {isAdmin && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}>
          <Tooltip title="Change page template">
            <Button
              size="small"
              startIcon={<Tune fontSize="small" />}
              onClick={() => setTemplateDialogOpen(true)}
              sx={{ textTransform: "none", borderRadius: "50px" }}
            >
              Change Template
            </Button>
          </Tooltip>
        </Box>
      )}
      <ThemedHeader stream={currentStream} theme={memorialTheme} />

      <TemplateSelectorDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        serviceType={currentStream.booking.service}
        streamId={currentStream.id}
        currentTemplateId={currentStream.template_id}
        onConfirm={() => {
          setTemplateDialogOpen(false);
          fetchStreamDetails();
        }}
      />

      <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ py: { xs: 2, md: 4 } }}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: hasSidePanel ? 8 : 12,
              }}
            >
              <StreamViewer
                streams={streams}
                activeStream={activeStreamIndex}
                onStreamChange={onStreamChange}
                memorialTheme={memorialTheme}
                currentStream={currentStream}
                deceasedName={deceasedData?.first_name}
                onDonationSuccess={handleDonationSuccess}
              />
              {!isMobile && (
                <Box>
                  <ShareControls
                    streamData={currentStream}
                    theme={memorialTheme}
                    donationsEnabled={currentStream?.go_fund_me_enabled || false}
                    streamProgram={streamProgram}
                    handleDownloadProgram={handleDownloadProgram}
                    downloadingProgram={downloadingProgram}
                    onDonationSuccess={handleDonationSuccess}
                    calendarEvent={calendarEvent}
                  />
                </Box>
              )}
            </Grid>

            {hasSidePanel && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack spacing={2}>
                  {showChemaDonationCard && (
                    <DonationSideCard
                      streamId={currentStream.id}
                      streamTitle={currentStream.title}
                      deceasedImage={currentStream.booking?.deceased?.[0]?.person_image}
                      refreshKey={donationRefreshKey}
                      onDonationSuccess={handleDonationSuccess}
                    />
                  )}
                  {showGoFundMeWidget && (
                    <GoFundMeWidget url={currentStream.go_fund_me_link!} />
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>

          {isMobile && (
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 1000,
                  mb: 2,
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleDownloadProgram}
                  sx={{ borderRadius: "20px" }}
                  disabled={!streamProgram}
                >
                  {downloadingProgram ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    <>
                      <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                        Program
                      </Typography>
                    </>
                  )}
                </Button>

                <IconButton
                  onClick={() => setShareDrawerOpen(true)}
                  sx={{
                    color: "#ffffff",
                    backgroundColor: memorialTheme.primary,
                  }}
                >
                  <Share />
                </IconButton>
              </Box>
            </motion.div>
          )}
          <ShareDrawer
            open={shareDrawerOpen}
            onClose={() => setShareDrawerOpen(false)}
            streamData={currentStream}
          />
          <motion.div variants={itemVariants}>
            <Box sx={{ boxShadow: 0, width: "100%" }}>
              {currentStream?.service?.type_of_service === "Church" ? null : (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {/* Tabs Section */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "50px",
                      backgroundColor: colors.primary[900],
                      width: "100%",
                      px: 2,
                      py: 1,
                      mt: 2,
                    }}
                  >
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      variant={isMobile ? "scrollable" : "fullWidth"}
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        "& .MuiTabs-flexContainer": {
                          width: "100%",
                          justifyContent: "space-between",
                        },
                        "& .MuiTab-root": {
                          flex: 1,
                          textTransform: "none",
                          fontSize: "1rem",
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          minHeight: "auto",
                          "&:hover": { color: colors.primary[700] },
                        },
                        "& .Mui-selected": {
                          color: `${colors.primary[900]} !important`,
                          backgroundColor: colors.primary[400],
                          borderRadius: "50px",
                          "&:hover": {
                            backgroundColor: memorialTheme.background,
                          },
                        },
                        "& .MuiTabs-indicator": { display: "none" },
                      }}
                    >
                      <Tab label="Obituary" />
                      <Tab label="Memories" />
                      <Tab label="Donations" />
                      <Tab label="Favourites" />
                      <Tab label="Timeline" />
                      <Tab label="Gallery" />
                      <Tab label={isStaging ? "Admin" : "Downloads"} />
                    </Tabs>
                  </Paper>

                  {/* Tab Panels */}
                  <Box sx={{ width: "100%", p: 0, m: 0 }}>
                    <TabPanel value={tabValue} index={0} sx={{ p: 0, m: 0 }}>
                      <ObituaryTab
                        deceasedData={deceasedData}
                        streamData={currentStream}
                        isLoadingDeceased={isLoadingDeceased}
                        isAdmin={isAdmin}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1} sx={{ p: 0, m: 0 }}>
                      <MemoriesTab
                        messages={messages}
                        isAdmin={isAdmin}
                        socketRef={socketRef}
                        isMobile={isMobile}
                        handleApprove={handleApprove}
                        handleDelete={handleDelete}
                        CommentCard={CommentCard}
                        colors={colors}
                        currentPage={currentPage}
                        totalCount={totalCount}
                        pageSize={pageSize}
                        hasNextPage={hasNextPage}
                        hasPreviousPage={hasPreviousPage}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onMessageSent={onMessageSent}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={3} sx={{ p: 0, m: 0 }}>
                      <FavouritesTab
                        deceasedId={deceasedData?.id}
                        deceasedFirstName={deceasedData?.first_name}
                        accentColor={memorialTheme.primary}
                        isAdmin={isAdmin}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={4} sx={{ p: 0, m: 0 }}>
                      <TimelineTab
                        deceasedId={deceasedData?.id}
                        deceasedFirstName={deceasedData?.first_name}
                        accentColor={memorialTheme.primary}
                        isAdmin={isAdmin}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={5} sx={{ p: 0, m: 0 }}>
                      <GalleryTab
                        deceasedId={currentStream.booking.deceased?.[0].id}
                        isAdmin={isAdmin}
                        isMobile={isMobile}
                        colors={colors}
                        socketRef={socketRef}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2} sx={{ p: 0, m: 0 }}>
                      <DonationsTab
                        streamData={currentStream}
                        streamId={currentStream?.id?.toString() || ""}
                        isAdmin={isAdmin}
                        isMobile={isMobile}
                        fetchStreamDetails={fetchStreamDetails}
                        donationRefreshKey={donationRefreshKey}
                        highlightDonationId={highlightDonationId}
                      />
                    </TabPanel>

                    <TabPanel value={tabValue} index={6} sx={{ p: 0, m: 0 }}>
                      {isStaging ? (
                        <AdminTab />
                      ) : (
                        <DownloadsTab
                          streamId={currentStream?.id?.toString() || ""}
                          vimeoLink={currentStream?.vimeo_stream_link || ""}
                          vimeoPassword={currentStream?.vimeo_download_password || ""}
                          currentStream={currentStream}
                        />
                      )}
                    </TabPanel>
                  </Box>
                </Box>
              )}
            </Box>
          </motion.div>
        </motion.div>
      </Container>
      <MemorialFooter
        theme={memorialTheme}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        facebookUrl={facebookUrl}
        linkedinUrl={linkedinUrl}
        youtubeUrl={youtubeUrl}
        whatsappUrl={whatsappUrl}
      />
    </Box>
  );
}
